<template>
    <v-app>
        <mainMenu></mainMenu>
        <v-container>
            <v-card>
                <v-card-title>
                    <font>พนักงานขับรถ</font>
                    <v-spacer></v-spacer>
                    <v-btn text color="orange darken-3" :loading="loading" @click="load">
                        <v-icon left>mdi-refresh</v-icon>โหลดใหม่
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-progress-linear v-if="loading" indeterminate color="orange accent-3"></v-progress-linear>
                <!-- Dimmed and click-proof while the list reloads, so nobody acts on a row
                     that is about to change. -->
                <v-card-text :class="{ reloading: loading }">
                    <div class="caption grey--text text--darken-1 mb-3">
                        รายชื่อมาจาก SAP — พนักงานขับรถคนใหม่ต้องเพิ่มใน SAP ก่อน แล้วจึงเปิดใช้งานที่หน้านี้
                    </div>

                    <v-alert v-if="loadError" type="error" outlined dense>{{ loadError }}</v-alert>
                    <v-alert v-if="loaded && !roster.sapAvailable" type="warning" outlined dense>
                        เชื่อมต่อ SAP ไม่ได้ — เปิดใช้งานไม่ได้ชั่วคราว ปิดใช้งานได้ตามปกติ
                    </v-alert>

                    <div class="table-scroll" v-if="roster.rows.length">
                        <v-simple-table>
                            <thead>
                                <tr>
                                    <th>รหัส</th>
                                    <th>ชื่อ (SAP)</th>
                                    <th>สถานะ</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in roster.rows" :key="row.code">
                                    <td>{{ row.code }}</td>
                                    <td>
                                        {{ row.sapName }}
                                        <div v-if="row.nameDiffers" class="caption orange--text text--darken-3">
                                            ในแอป: {{ row.firestoreName }} — แก้ชื่อใน SAP
                                        </div>
                                    </td>
                                    <td>
                                        <v-chip small dark :color="statusOf(row.status).color">{{ statusOf(row.status).label }}</v-chip>
                                    </td>
                                    <td class="text-right actions">
                                        <template v-if="row.status === 'enabled' || row.status === 'nameMismatch'">
                                            <v-btn small text @click="openPin('reset', row)">
                                                <v-icon left small>mdi-dialpad</v-icon>รีเซ็ต PIN
                                            </v-btn>
                                            <v-btn small text color="red" @click="openDisable(row)">ปิดใช้งาน</v-btn>
                                        </template>
                                        <template v-else-if="row.status === 'incomplete' || row.status === 'notEnabled'">
                                            <v-btn v-if="row.canEnable" small dark color="green" @click="openPin('enable', row)">เปิดใช้งาน</v-btn>
                                            <span v-else class="caption orange--text text--darken-3">แก้ชื่อใน SAP ก่อน</span>
                                            <v-btn v-if="row.status === 'incomplete'" small text color="red" @click="openDisable(row)">ปิดใช้งาน</v-btn>
                                        </template>
                                        <span v-else class="caption red--text">แจ้งผู้ดูแลระบบให้แก้ข้อมูลซ้ำ</span>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </div>

                    <template v-if="roster.outOfSap.length">
                        <div class="subtitle-1 mt-6">{{ roster.sapAvailable ? 'ไม่ตรงกับ SAP' : 'ใช้งานอยู่ในแอป' }}</div>
                        <div class="caption grey--text text--darken-1 mb-2">
                            {{ roster.sapAvailable
                                ? 'เปิดใช้งานอยู่ในแอป แต่ SAP ไม่มีรหัสนี้ หรือยกเลิกแล้ว'
                                : 'เชื่อมต่อ SAP ไม่ได้ จึงแสดงพนักงานขับรถทุกคนที่เปิดใช้งานอยู่ในแอป' }}
                        </div>
                        <div class="table-scroll">
                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th>รหัส</th>
                                        <th>ชื่อในแอป</th>
                                        <th>ใน SAP</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in roster.outOfSap" :key="'out-' + row.code">
                                        <td>{{ row.code }}</td>
                                        <td>{{ row.firestoreName || '—' }}</td>
                                        <td>{{ roster.sapAvailable ? (row.sapName || 'ไม่มีใน SAP') : '—' }}</td>
                                        <td class="text-right actions">
                                            <v-btn small text color="red" @click="openDisable(row)">ปิดใช้งาน</v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </div>
                    </template>

                    <div v-if="roster.hiddenSapCount" class="caption grey--text mt-4">
                        ซ่อน {{ roster.hiddenSapCount }} รายการใน SAP (ยกเลิก / ไม่ใช้ / ลูกค้ามารับเอง)
                    </div>
                </v-card-text>
            </v-card>

            <!-- เปิดใช้งาน / รีเซ็ต PIN -->
            <v-dialog v-model="pinDialog.show" max-width="400px" persistent>
                <v-card>
                    <v-progress-linear v-if="busy" indeterminate color="orange accent-3"></v-progress-linear>
                    <v-card-title>{{ pinDialog.action === 'enable' ? 'เปิดใช้งานแอปพนักงานขับรถ' : 'รีเซ็ต PIN พนักงานขับรถ' }}</v-card-title>
                    <v-card-text>
                        <v-text-field label="พนักงานขับรถ:" :value="pinDialog.label" color="orange accent-3" readonly></v-text-field>
                        <v-radio-group v-model="pinDialog.mode" :disabled="busy" class="mt-0">
                            <v-radio label="สุ่ม PIN ใหม่ให้" value="random" color="orange accent-3"></v-radio>
                            <v-radio label="กำหนด PIN เอง" value="manual" color="orange accent-3"></v-radio>
                        </v-radio-group>
                        <v-text-field v-if="pinDialog.mode === 'manual'" v-model="pinDialog.pin" ref="pinField"
                            label="PIN ใหม่ (ตัวเลข 6 หลัก):" maxlength="6" inputmode="numeric"
                            :rules="pinRules" :disabled="busy" color="orange accent-3" autocomplete="off"></v-text-field>
                        <div class="caption grey--text text--darken-1">
                            PIN ต้องไม่ซ้ำกับพนักงานขับรถคนอื่น และไม่ใช่เลขซ้ำหรือเลขเรียง เช่น 111111 หรือ 123456
                            <template v-if="pinDialog.action === 'reset'">
                                PIN เดิมจะใช้ไม่ได้ทันที และโทรศัพท์ที่เข้าระบบอยู่จะถูกออกจากระบบ
                            </template>
                        </div>
                        <div v-if="busy" class="mt-3 orange--text text--darken-3">
                            {{ pinDialog.action === 'enable' ? 'กำลังเปิดใช้งาน… อาจใช้เวลาสักครู่' : 'กำลังรีเซ็ต PIN…' }}
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="red" dark :disabled="busy" @click="closePin">ยกเลิก</v-btn>
                        <!-- :loading alone: Vuetify greys out a disabled button's spinner until it
                             is barely visible, and .v-btn--loading already blocks clicks. The
                             busy guard in confirmPin is what stops a second request. -->
                        <v-btn color="green" dark :loading="busy" @click="confirmPin">ตกลง</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- ปิดใช้งาน -->
            <v-dialog v-model="disableDialog.show" max-width="400px" persistent>
                <v-card>
                    <v-progress-linear v-if="busy" indeterminate color="orange accent-3"></v-progress-linear>
                    <v-card-title class="red--text">ปิดใช้งานแอปพนักงานขับรถ</v-card-title>
                    <v-card-text>
                        <div class="subtitle-1 mb-2">{{ disableDialog.label }}</div>
                        <div>พนักงานขับรถจะถูกออกจากระบบ และเข้าแอปไม่ได้จนกว่าจะเปิดใช้งานอีกครั้งพร้อม PIN ใหม่</div>
                        <div v-if="disableDialog.nameDiffers" class="mt-1">ชื่อในแอปไม่ตรงกับ SAP — จะเปิดใช้งานอีกครั้งได้หลังแก้ชื่อใน SAP แล้ว</div>
                        <div v-if="busy" class="mt-3 orange--text text--darken-3">กำลังปิดใช้งาน… อาจใช้เวลาสักครู่</div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text :disabled="busy" @click="closeDisable">ยกเลิก</v-btn>
                        <!-- :loading alone, for the same reason as the ตกลง button above. -->
                        <v-btn color="red" dark :loading="busy" @click="confirmDisable">ปิดใช้งาน</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- PIN ที่สุ่มได้ -- แสดงครั้งเดียว ปิดแล้วไม่มีทางดูได้อีก -->
            <v-dialog v-model="pinResult.show" max-width="360px" persistent>
                <v-card>
                    <v-card-title>PIN ใหม่ของ {{ pinResult.label }}</v-card-title>
                    <v-card-text class="text-center">
                        <div class="display-2 font-weight-bold my-4" style="letter-spacing: 0.3em">{{ pinResult.pin }}</div>
                        <div>แจ้ง PIN นี้กับพนักงานขับรถโดยตรง</div>
                        <div class="red--text">PIN จะแสดงเพียงครั้งเดียว ปิดหน้าต่างนี้แล้วจะดูอีกไม่ได้</div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="green" dark @click="closePinResult">จดแล้ว ปิด</v-btn>
                        <v-spacer></v-spacer>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="errorDialog.show" max-width="440px">
                <v-card>
                    <v-card-text class="text-center pt-6">
                        <v-icon color="red" :size="45">mdi-close-circle-outline</v-icon>
                        <div class="mt-2">{{ errorDialog.message }}</div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="errorDialog.show = false">ปิด</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-snackbar v-model="snackbar.show" color="green" :timeout="2500">{{ snackbar.text }}</v-snackbar>
        </v-container>
    </v-app>
</template>

<script>
import { functions } from '@/firebase'
import mainMenu from '@/components/mainMenu'

// Keys are functions/driverRoster.js STATUSES; npm run test:drivers pins that list.
const STATUS = {
    enabled: { label: 'ใช้งานแอปได้', color: 'green' },
    nameMismatch: { label: 'ชื่อไม่ตรง SAP', color: 'orange darken-2' },
    incomplete: { label: 'ตั้งค่าไม่ครบ', color: 'amber darken-3' },
    notEnabled: { label: 'ยังไม่เปิดใช้', color: 'grey' },
    duplicate: { label: 'รหัสซ้ำใน Firestore', color: 'red' }
}

function emptyRoster () {
    return { sapAvailable: true, rows: [], outOfSap: [], hiddenSapCount: 0 }
}

function emptyPinDialog () {
    return { show: false, action: '', code: '', sapName: '', uid: '', label: '', mode: 'random', pin: '' }
}

function emptyDisableDialog () {
    return { show: false, code: '', label: '', nameDiffers: false }
}

function emptyPinResult () {
    return { show: false, label: '', pin: '' }
}

export default {
    components: {
        mainMenu
    },
    data () {
        return {
            STATUS,
            roster: emptyRoster(),
            loading: false,
            loaded: false,
            loadError: '',
            busy: false,
            pinDialog: emptyPinDialog(),
            disableDialog: emptyDisableDialog(),
            pinResult: emptyPinResult(),
            errorDialog: { show: false, message: '' },
            snackbar: { show: false, text: '' },
            // The callables enforce the full PIN rules; this only catches a typo early.
            pinRules: [v => /^\d{6}$/.test(v || '') || 'PIN ต้องเป็นตัวเลข 6 หลัก']
        }
    },
    methods: {
        rowLabel (row) {
            return row.code + ' · ' + (row.firestoreName || row.sapName || '')
        },

        // An unknown status (STATUS is keyed by functions/driverRoster.js
        // STATUSES; a mismatch between the two would otherwise throw here)
        // renders as a plain grey chip labelled with the raw value.
        statusOf (status) {
            return STATUS[status] || { label: status, color: 'grey' }
        },

        // The callables write their refusals in Thai; anything else (network, a
        // function not deployed yet) gets a generic line.
        callErrorText (prefix, error) {
            const thai = error && error.message && /[฀-๿]/.test(error.message)
            return prefix + ' : ' + (thai ? error.message : 'เชื่อมต่อระบบไม่ได้ โปรดลองใหม่')
        },

        load () {
            this.loading = true
            this.loadError = ''
            return functions.httpsCallable('driverRoster')()
                .then(res => {
                    this.roster = Object.assign(emptyRoster(), res.data)
                    this.loaded = true
                })
                .catch(error => {
                    this.loadError = this.callErrorText('โหลดรายชื่อพนักงานขับรถไม่สำเร็จ', error)
                })
                .then(() => {
                    this.loading = false
                })
        },

        showError (prefix, error) {
            this.errorDialog = { show: true, message: this.callErrorText(prefix, error) }
        },

        flash (text) {
            this.snackbar = { show: true, text: text }
        },

        // action: 'enable' sends the row's code and SAP name exactly as the roster
        // returned them -- enableDriver compares that name with SAP again.
        // action: 'reset' uses the existing resetDriverPin, by account uid.
        openPin (action, row) {
            this.pinDialog = Object.assign(emptyPinDialog(), {
                show: true,
                action: action,
                code: row.code,
                sapName: row.sapName,
                uid: row.uid || '',
                label: this.rowLabel(row)
            })
        },

        closePin () {
            this.pinDialog = emptyPinDialog()
        },

        confirmPin () {
            if (this.busy) return
            const d = this.pinDialog
            const manual = d.mode === 'manual'
            if (manual && !this.$refs.pinField.validate(true)) {
                this.$refs.pinField.focus()
                return
            }
            const pin = manual ? d.pin : null
            const enabling = d.action === 'enable'
            const call = enabling
                ? functions.httpsCallable('enableDriver')({ code: d.code, sapName: d.sapName, pin: pin })
                : functions.httpsCallable('resetDriverPin')({ uid: d.uid, pin: pin })
            const label = d.label
            this.busy = true
            call
                .then(res => {
                    this.closePin()
                    if (res.data && res.data.pin) {
                        this.pinResult = { show: true, label: label, pin: res.data.pin }
                    } else {
                        this.flash(enabling ? 'เปิดใช้งานแล้ว' : 'รีเซ็ต PIN แล้ว')
                    }
                    this.load()
                })
                .catch(error => {
                    this.showError(enabling ? 'เปิดใช้งานไม่สำเร็จ' : 'รีเซ็ต PIN ไม่สำเร็จ', error)
                })
                .then(() => {
                    this.busy = false
                })
        },

        // Drops the PIN from the component too, not only from the screen.
        closePinResult () {
            this.pinResult = emptyPinResult()
        },

        openDisable (row) {
            this.disableDialog = { show: true, code: row.code, label: this.rowLabel(row), nameDiffers: !!row.nameDiffers }
        },

        closeDisable () {
            this.disableDialog = emptyDisableDialog()
        },

        confirmDisable () {
            if (this.busy) return
            this.busy = true
            functions.httpsCallable('disableDriver')({ code: this.disableDialog.code })
                .then(() => {
                    this.closeDisable()
                    this.flash('ปิดใช้งานแล้ว')
                })
                .catch(error => {
                    // A partial disable (password not replaced) still changed the
                    // flags, so the roster is reloaded either way.
                    this.closeDisable()
                    this.showError('ปิดใช้งานไม่สำเร็จ', error)
                })
                .then(() => {
                    this.busy = false
                    this.load()
                })
        }
    },
    mounted () {
        this.load()
    }
}
</script>

<style scoped>
.table-scroll {
    overflow-x: auto;
}
.actions {
    white-space: nowrap;
}
.reloading {
    opacity: 0.5;
    pointer-events: none;
    transition: opacity 0.2s;
}
</style>
